/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Point, Rectangle } from "src/classes/plate/lib/types";
import { ONE_GRID_UNIT } from "src/lib/constants";

export class MathUtils {
  public static getRectangleFromPoints(p1: Point, p2: Point): Rectangle {
    return {
      top: Math.min(p1.y, p2.y),
      left: Math.min(p1.x, p2.x),
      bottom: Math.max(p1.y, p2.y),
      right: Math.max(p1.x, p2.x),
    };
  }

  public static constrainValueToBounds = (value: number, min: number, max: number): number => {
    // max value is greater of test and minimum
    // min value is lesser of greater of above and maximum
    return Math.min(Math.max(value, min), max);
  };

  public static roundNumToNearest(num: number, nearest: number): number {
    return Math.round(num / nearest) * nearest;
  }

  public static getDistanceBetweenPoints(
    p1: Point,
    p2: Point,
    lockToMainAxis = false,
    aspectRatio: number | null = null,
    roundTo = ONE_GRID_UNIT
  ): { dx: number; dy: number } {
    const naiveDx = p1.x - p2.x; // the x change, ignoring any constraints
    const naiveDy = p1.y - p2.y; // the y change, ignoring any constraints
    const mainAxis = Math.abs(naiveDx) > Math.abs(naiveDy) ? "X" : "Y"; // whether the move is more horizontal or vertical

    let dx = naiveDx;
    let dy = naiveDy;
    if (aspectRatio) {
      dx = mainAxis === "X" ? naiveDx : Math.round(naiveDy * aspectRatio); // if dragging mostly vertically, x change is computed from y change
      dy = mainAxis === "Y" ? naiveDy : Math.round(naiveDx / aspectRatio); // if dragging mostly horizontally, y change is computed from x change
    } else if (lockToMainAxis) {
      dx = mainAxis === "X" ? naiveDx : 0; // if dragging mostly vertically x change is 0
      dy = mainAxis === "Y" ? naiveDy : 0; // if dragging mostly horizontally y change is 0
    }
    return { dx: this.roundNumToNearest(dx, roundTo), dy: this.roundNumToNearest(dy, roundTo) };
  }
}
